import { useCallback, useRef, useState } from 'react';
import { erc20Abi, getAddress } from 'viem';
import { switchChain, writeContract } from 'wagmi/actions';

import { toast } from '@/components/ui/toast';
import { runWithFundAuthorization } from '@/features/fundAuthorization';
import {
    HYPER_EVM_CHAIN_ID,
    useAnchorChain,
    useAuthedApi,
    useFactionsAuthedApi,
    useWallet,
    walletWagmiConfig,
} from '@/features/wallet';
import { wolfswapFactionsApi } from '@/lib/api';
import {
    fetchDepositQuote,
    fetchSpeedJob,
    cancelSpeedJob,
    recordSpeedJobTxHashes,
    startDeposit,
} from '@/lib/api/speed';
import type {
    BridgeQuoteResponse,
    SpeedJobResponse,
    StartDepositRequest,
} from '@/lib/api/speed';
import {
    describeErrorText,
    getErrorMessage,
    isApiError,
    isUserRejection,
} from '@/lib/errors';

import { bridgeRequestField } from '../bridgeOverride';
import { depositBridgeInfoFromJob } from '../services/bridgeFees';
import { isCustodialWalletMissingError } from '../services/custodialSetup';
import { executeRelaySteps } from '../services/custodialTx';
import {
    ANCHOR_DEPOSIT_TOKENS,
    defaultAnchorSource,
    isAnchorSource,
    resolveHyperUsdcAddress,
} from '../services/directDeposit';
import type { DepositSource } from '../services/directDeposit';
import {
    reportJobException,
    setActiveJobForErrorReports,
} from '../services/jobErrorReporter';
import type { CustodialDepositState, CustodialDepositStatus } from '../types';

import {
    ensureHyperEvmChain,
    waitForSuccessfulHyperEvmReceipt,
} from './hyperEvmChain';

const TOAST_DURATION = 10_000;

/** Poll the backend deposit job/transfer status every 3s for up to 10 minutes. */
const STATUS_POLL_INTERVAL_MS = 3_000;
const STATUS_POLL_TIMEOUT_MS = 10 * 60_000;

/** One re-try of the tx-hash POST, long enough for a moment-early race to settle. */
const TX_HASH_RETRY_DELAY_MS = 1_500;

/**
 * Codes the backend answers with when the transfer has already moved past Pending by the time the
 * hashes are posted (its own status listener, or a concurrent /recheck, got there first). The
 * deposit is fine in every one of those cases, so they must never reach the user.
 */
const TX_HASH_RACE_CODES = new Set([
    'notFound',
    'depositNotAwaitingSignature',
    'depositStateChanged',
]);

const INITIAL_STATE: CustodialDepositState = {
    status: 'idle',
    amount: null,
    error: null,
    transferId: null,
    jobId: null,
    txHash: null,
    progress: null,
    progressMessage: null,
    progressStep: null,
    needsOnboarding: false,
    bridge: null,
    bridgeFeeUsd: null,
    estimatedReceive: null,
};

function logStep(step: string, data?: unknown) {
    console.log(`[CustodialDeposit] ${step}`, data ?? '');
}

/**
 * The trading wallet's own USDC is moved by the backend saga; every other
 * source is the user's EOA signing bridge steps.
 */
function depositModeFor(
    source: DepositSource | undefined,
): StartDepositRequest['mode'] {
    return source === 'walletBalance' ? 'custodialBalance' : 'metamask';
}

const sleep = async (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

function transferIdFromJob(job: SpeedJobResponse): number | null {
    return job.transferId ?? job.response?.transferId ?? null;
}

function txHashesFromJob(job: SpeedJobResponse): string[] {
    const hashes = job.response?.txHashes;
    return Array.isArray(hashes)
        ? hashes.filter((hash): hash is string => typeof hash === 'string')
        : [];
}

function txHashFromJob(job: SpeedJobResponse): string | null {
    return txHashesFromJob(job).at(-1) ?? null;
}

function hasExecutableRelaySteps(steps: unknown): boolean {
    return (
        Array.isArray(steps) &&
        steps.some(
            (step) =>
                Array.isArray(step?.items) &&
                step.items.some((item: { data?: unknown }) => !!item?.data),
        )
    );
}

/**
 * The HyperEVM-origin deposit the backend answers with when it onboards the user server-side
 * (`route: 'hyperevmDirect'`): no Relay steps and no requestId, just a plain ERC20 transfer the
 * user signs on chain 999 into the custodial wallet the saga provisioned. Everything after the
 * transfer — hash recording, job polling — is the normal deposit path.
 */
function hyperDirectTransfer(job: SpeedJobResponse): {
    token: `0x${string}`;
    to: `0x${string}`;
    amount: bigint;
} | null {
    const response = job.response;
    if (!response || response.route !== 'hyperevmDirect') {
        return null;
    }
    const { destinationAddress, destinationCurrency, amount } = response;
    if (
        typeof destinationAddress !== 'string' ||
        typeof destinationCurrency !== 'string' ||
        typeof amount !== 'string' ||
        !/^\d+$/.test(amount)
    ) {
        return null;
    }
    return {
        token: getAddress(destinationCurrency),
        to: getAddress(destinationAddress),
        amount: BigInt(amount),
    };
}

function jobNeedsSignature(job: SpeedJobResponse, signedRelaySteps: boolean) {
    if (
        signedRelaySteps ||
        job.kind !== 'deposit' ||
        txHashesFromJob(job).length > 0
    ) {
        return false;
    }
    if (job.response?.route === 'hyperevmDirect') {
        return !!hyperDirectTransfer(job);
    }
    return (
        job.response?.route === 'metamask' &&
        hasExecutableRelaySteps(job.response?.steps)
    );
}

function amountFromJob(job: SpeedJobResponse): bigint | null {
    const raw = job.response?.amount;
    if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
        return null;
    }
    return BigInt(raw);
}

function failureFromJob(job: SpeedJobResponse): Error {
    // a refunded job carries the bridge's own account when the backend has it (why it refunded and
    // where the money went back to); the bare sentence is the fallback, never the override
    const message =
        job.error ||
        (job.status === 'refunded'
            ? 'Deposit was refunded to your wallet.'
            : 'Deposit failed.');
    const error = new Error(message) as Error & { code?: string | null };
    error.code = job.code;
    return error;
}

function codeFromError(error: unknown): string | null {
    if (isApiError(error)) return error.body?.code ?? null;
    return typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code?: unknown }).code ?? '')
        : null;
}

/**
 * True for the answers that mean "the hashes arrived a moment late", as opposed to a real refusal.
 * Only used to label the log line: once the wallet has broadcast, no answer to this call is worth
 * failing the deposit over.
 */
function isTxHashRace(error: unknown): boolean {
    const code = codeFromError(error);
    if (code && TX_HASH_RACE_CODES.has(code)) {
        return true;
    }
    return (
        isApiError(error) &&
        (error.status === 404 || error.status === 409 || error.status === 422)
    );
}

function friendlyDepositError(error: unknown): string {
    if (codeFromError(error) === 'voucherRequired') {
        return 'Enter a launch voucher and wait for your wallet setup lane, then retry the deposit.';
    }
    if (isCustodialWalletMissingError(error)) {
        return (
            'Finish custodial wallet setup first, then return here and retry ' +
            'the deposit.'
        );
    }
    return describeErrorText(error, { operation: 'Deposit' });
}

interface UseCustodialDepositParams {
    /** Called after the deposit saga settles successfully. */
    onSuccess?: () => void;
    /**
     * USDC the custodial wallet holds on HyperEVM (display units) as the client last read it.
     * Declared to the fund-authorization ceremony as the amount the server may sweep on top of
     * the deposit; null when unknown (then nothing above the deposit amount is accepted).
     */
    heldOnHyperEvm?: () => number | null;
}

interface UseCustodialDepositResult {
    state: CustodialDepositState;
    status: CustodialDepositStatus;
    /** Begin the deposit flow — opens the entering-amount state. */
    open: () => void;
    /**
     * Confirm the amount (USDC base units, 6dp) and run the deposit saga:
     * start → sign in wallet → poll until settled. `source` picks the origin: Cronos USDC or
     * USDC.e (Relay steps; USDC.e is the default) or HyperEVM (a single ERC20 transfer —
     * `route: 'hyperevmDirect'`, used for users with no adapter yet).
     */
    confirm: (amount: bigint, source: DepositSource) => Promise<void>;
    /**
     * Display pre-quote for an amount the user is still typing, for the source
     * they picked: which bridge would carry it, what it would cost and what
     * would land. Read-only, reserves nothing, and never carried into
     * execution. Feeds the form's estimate, its native-fee warning and the gate
     * on its button.
     */
    quoteDeposit: (
        amount: bigint,
        source: DepositSource | undefined,
        signal: AbortSignal,
    ) => Promise<BridgeQuoteResponse>;
    /** Cancel from enteringAmount / failed → back to idle. */
    cancel: () => void;
    /** Retry after a failure — returns to enteringAmount. */
    retry: () => void;
    /** Reset back to idle (e.g. after success). */
    reset: () => void;
    /** Attach UI state to an existing backend deposit job without prompting signatures. */
    attach: (job: SpeedJobResponse) => Promise<void>;
    /** Continue the attached job when a wallet signature is required. */
    continueActive: () => Promise<void>;
    canContinue: boolean;
    /**
     * Cancel a job still waiting on the wallet signature. Nothing has moved at that point, and the
     * backend refuses (`depositInFlight`) the moment something has, so this can never strand funds.
     */
    cancelActive: () => Promise<void>;
    canCancel: boolean;
    isActive: boolean;
}

/**
 * State machine for custodial ("Quick Trade") deposits via the NEW Relay saga.
 *
 * States: idle → enteringAmount → submitting → awaitingSignature →
 *         bridging → success / failed
 *
 * 1. `startDeposit` (mode: 'metamask') returns ordered Relay `steps`.
 * 2. Each step is signed in the user's wallet on the origin chain (Cronos).
 * 3. The backend transfer is polled until `Settled` (success) or
 *    `Refunded` / `Failed` (failure).
 *
 * On success, calls `onSuccess` (typically a balance refetch).
 */
export function useCustodialDeposit({
    onSuccess,
    heldOnHyperEvm,
}: UseCustodialDepositParams = {}): UseCustodialDepositResult {
    const [state, setState] = useState<CustodialDepositState>(INITIAL_STATE);
    const lockRef = useRef(false);
    const authedApi = useAuthedApi();
    // job status reads moved to factions.backend's speed facet (read-only);
    // startDeposit + tx-hash recording (sensitive/saga writes) stay on MoonHub.API.
    const factionsApi = useFactionsAuthedApi();
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();

    const open = useCallback(() => {
        if (state.status !== 'idle') return;
        setState({ ...INITIAL_STATE, status: 'enteringAmount' });
    }, [state.status]);

    const cancel = useCallback(() => {
        if (state.status === 'enteringAmount' || state.status === 'failed') {
            setState(INITIAL_STATE);
        }
    }, [state.status]);

    const retry = useCallback(() => {
        if (state.status === 'failed') {
            setState((prev) => ({
                ...prev,
                status: 'enteringAmount',
                error: null,
                txHash: null,
                transferId: null,
                jobId: null,
                progress: null,
                progressMessage: null,
                needsOnboarding: false,
            }));
        }
    }, [state.status]);

    const reset = useCallback(() => {
        setState(INITIAL_STATE);
    }, []);

    /**
     * The one signature the `hyperevmDirect` route needs: an ERC20 transfer of the job's own
     * amount into the custodial wallet, on chain 999. The wallet is put back on the anchor
     * right after — the rest of the app (and the job polling below) gates on it.
     */
    const sendHyperDirectTransfer = useCallback(
        async (transfer: {
            token: `0x${string}`;
            to: `0x${string}`;
            amount: bigint;
        }): Promise<string> => {
            try {
                // explicit switch: injected wallets are on the anchor here, and writeContract
                // with a chainId asserts rather than switches for them.
                await ensureHyperEvmChain();
                const hash = await writeContract(walletWagmiConfig, {
                    ...(address ? { account: getAddress(address) } : {}),
                    address: transfer.token,
                    abi: erc20Abi,
                    functionName: 'transfer',
                    args: [transfer.to, transfer.amount],
                    chainId: HYPER_EVM_CHAIN_ID,
                });
                setState((prev) => ({ ...prev, txHash: hash }));
                await waitForSuccessfulHyperEvmReceipt(hash, 'Deposit transfer');
                return hash;
            } finally {
                switchChain(walletWagmiConfig, {
                    chainId: anchorChainId,
                }).catch(() => undefined);
            }
        },
        [address, anchorChainId],
    );

    /**
     * Hand the broadcast hashes to the backend. Returns the job it answers with, or `null` when it
     * could not be told at all — the caller then keeps polling, which is how the deposit finishes
     * anyway. Never throws: the wallet has already spent the user's funds by the time this runs.
     */
    const recordTxHashes = useCallback(
        async (
            jobId: string,
            hashes: string[],
        ): Promise<SpeedJobResponse | null> => {
            if (!authedApi) return null;
            try {
                return await recordSpeedJobTxHashes(authedApi, jobId, hashes);
            } catch (error) {
                logStep('Tx-hash recording failed, retrying once', {
                    jobId,
                    race: isTxHashRace(error),
                    error: getErrorMessage(error),
                });
            }
            await sleep(TX_HASH_RETRY_DELAY_MS);
            try {
                return await recordSpeedJobTxHashes(authedApi, jobId, hashes);
            } catch (error) {
                logStep('Tx-hash recording gave up, polling the job instead', {
                    jobId,
                    race: isTxHashRace(error),
                    error: getErrorMessage(error),
                });
                return null;
            }
        },
        [authedApi],
    );

    const trackDepositJob = useCallback(
        async (
            initialJob: SpeedJobResponse,
            amount: bigint | null,
            options: { signRequiredSteps: boolean },
        ) => {
            if (!authedApi || !factionsApi) {
                throw new Error('Connect & sign in first');
            }
            let deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
            let job = initialJob;
            let signedRelaySteps = false;

            for (;;) {
                const transferId = transferIdFromJob(job);
                const txHash = txHashFromJob(job);
                const needsSignature = jobNeedsSignature(job, signedRelaySteps);
                // which bridge the backend picked and what it expects to land.
                // Sticky: a later poll that omits the fields must not blank a
                // figure the form is already showing.
                const bridgeInfo = depositBridgeInfoFromJob(job);

                setState((prev) => ({
                    ...prev,
                    amount: amount ?? prev.amount,
                    status: needsSignature
                        ? 'awaitingSignature'
                        : job.status === 'bridging'
                            ? 'bridging'
                            : job.status === 'settled'
                                ? 'success'
                                : job.status === 'failed' ||
                                job.status === 'refunded'
                                    ? 'failed'
                                    : signedRelaySteps
                                        ? 'bridging'
                                        : 'submitting',
                    jobId: job.jobId,
                    transferId,
                    progress: job.progress,
                    progressMessage: job.message,
                    progressStep: job.step,
                    txHash: txHash ?? prev.txHash,
                    originChainId:
                        typeof job.response?.originChainId === 'number'
                            ? job.response.originChainId
                            : prev.originChainId ?? null,
                    route:
                        typeof job.response?.route === 'string'
                            ? job.response.route
                            : prev.route ?? null,
                    bridge: bridgeInfo.bridge ?? prev.bridge ?? null,
                    bridgeFeeUsd:
                        bridgeInfo.bridgeFeeUsd ?? prev.bridgeFeeUsd ?? null,
                    estimatedReceive:
                        bridgeInfo.estimatedReceive ??
                        prev.estimatedReceive ??
                        null,
                }));
                logStep('Deposit job status', {
                    jobId: job.jobId,
                    status: job.status,
                    step: job.step,
                });

                if (job.status === 'settled') {
                    setState((prev) => ({
                        ...prev,
                        amount: amount ?? prev.amount,
                        status: 'success',
                        txHash: txHash ?? prev.txHash,
                        progress: 100,
                        progressMessage: 'Deposit confirmed',
                    }));
                    toast.success('Deposit successful!', {
                        duration: TOAST_DURATION,
                    });
                    onSuccess?.();
                    return;
                }

                if (job.status === 'failed' || job.status === 'refunded') {
                    throw failureFromJob(job);
                }

                if (needsSignature) {
                    const direct = hyperDirectTransfer(job);
                    const steps = job.response?.steps ?? [];
                    if (!direct && !hasExecutableRelaySteps(steps)) {
                        throw new Error('Deposit signature step is missing.');
                    }

                    setState((prev) => ({
                        ...prev,
                        status: 'awaitingSignature',
                        progress: job.progress,
                        progressMessage: job.message,
                        progressStep: job.step,
                    }));

                    if (!options.signRequiredSteps) {
                        return;
                    }

                    const hashes = direct
                        ? [await sendHyperDirectTransfer(direct)]
                        : await executeRelaySteps(
                            walletWagmiConfig,
                            steps,
                            ({ hash }) => {
                                setState((prev) => ({ ...prev, txHash: hash }));
                            },
                        );
                    // The money is on chain from this line on. Recording the hashes only tells the
                    // backend where to look, and it can legitimately be refused (the transfer may
                    // already have advanced past Pending). Failing the deposit here would tell the
                    // user their funds were lost while the saga quietly settles them, so the call
                    // is best effort: one re-try, then fall back to plain job polling.
                    const signedJobId = job.jobId;
                    let nextJob = await recordTxHashes(signedJobId, hashes);
                    if (!nextJob) {
                        // recording never got through, so read the job back instead; if even that
                        // fails the loop below keeps polling on its own schedule.
                        try {
                            nextJob = await fetchSpeedJob(
                                factionsApi,
                                signedJobId,
                            );
                        } catch (error) {
                            logStep('Job re-fetch after recording failed', {
                                jobId: signedJobId,
                                error: getErrorMessage(error),
                            });
                        }
                    }
                    // eslint-disable-next-line require-atomic-updates -- one sequential poll loop; nothing else writes it
                    signedRelaySteps = true;
                    if (nextJob) {
                        // eslint-disable-next-line require-atomic-updates -- one sequential poll loop; nothing else writes it
                        job = nextJob;
                    }
                    // The deadline was armed before the wallet prompt, and the prompt can stay open
                    // for as long as the user likes. A slow signer used to be shown "taking longer
                    // than expected" the instant they finished signing, for a deposit that then
                    // bridged fine. The bridge gets its full window from the moment the money left.
                    deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
                    setState((prev) => ({
                        ...prev,
                        status: 'bridging',
                        progress: Math.max(prev.progress ?? 0, 75),
                        progressMessage: 'Waiting for bridge confirmation',
                    }));
                    logStep('Relay steps submitted', { hashes });
                }

                if (Date.now() >= deadline) {
                    throw new Error(
                        'Deposit is taking longer than expected. ' +
                            'It may still complete, check your balance shortly.',
                    );
                }

                await sleep(STATUS_POLL_INTERVAL_MS);
                // eslint-disable-next-line require-atomic-updates -- one sequential poll loop; nothing else writes it
                job = await fetchSpeedJob(factionsApi, initialJob.jobId);
            }
        },
        [
            authedApi,
            factionsApi,
            onSuccess,
            recordTxHashes,
            sendHyperDirectTransfer,
        ],
    );

    const failDeposit = useCallback((error: unknown) => {
        const message = friendlyDepositError(error);
        // Balances must be re-read on FAILURE too, not just success: a deposit can fail after its
        // origin transaction has already gone through (a stuck bridge, a failed funding leg), so
        // the wallet has really been debited and showing the pre-deposit figure is simply wrong.
        onSuccess?.();

        if (isUserRejection(message)) {
            logStep('User rejected');
            setState((prev) => ({
                ...prev,
                status: 'failed',
                error: 'Transaction cancelled.',
            }));
            return;
        }

        console.error('[CustodialDeposit] failed:', error);
        const code = codeFromError(error);
        const voucherRequired = code === 'voucherRequired';
        setState((prev) => ({
            ...prev,
            status: 'failed',
            error: message,
            needsOnboarding:
                isCustodialWalletMissingError(error) ||
                code === 'custodialWalletMissing' ||
                voucherRequired,
            onboardingStep: voucherRequired ? 'voucher' : 'wallet',
        }));
        toast.error('Deposit did not complete', {
            description: message,
            duration: TOAST_DURATION,
        });
    }, [onSuccess]);

    /**
     * Where a deposit for this source actually starts. Shared by the start call
     * and the pre-quote so the two can never price different things: a quote
     * for USDC.e that starts a deposit of USDC would be a fee shown for a route
     * the user never took.
     *
     * A HyperEVM-origin deposit moves USDC the user already holds on 999; the
     * token address comes from the backend (balance/light, or the public config
     * for users it has not onboarded yet), never hardcoded.
     */
    const resolveDepositOrigin = useCallback(
        async (
            source: DepositSource | undefined,
        ): Promise<{ chainId: number; currency: string }> => {
            if (source === 'hyperEvm' || source === 'walletBalance') {
                if (!factionsApi) {
                    throw new Error('Connect & sign in first');
                }
                return {
                    chainId: HYPER_EVM_CHAIN_ID,
                    currency: await resolveHyperUsdcAddress(
                        factionsApi,
                        wolfswapFactionsApi,
                    ),
                };
            }
            // the anchor's own stable; Cronos has two USDCs and the form picked
            // whichever the user holds
            const token =
                ANCHOR_DEPOSIT_TOKENS[
                    source !== undefined && isAnchorSource(source)
                        ? source
                        : defaultAnchorSource(anchorChainId)
                ];
            return { chainId: token.chainId, currency: token.address };
        },
        [anchorChainId, factionsApi],
    );

    const quoteDeposit = useCallback(
        async (
            amount: bigint,
            source: DepositSource | undefined,
            signal: AbortSignal,
        ) => {
            if (!authedApi) {
                throw new Error('Connect & sign in first');
            }
            const origin = await resolveDepositOrigin(source);
            return fetchDepositQuote(
                authedApi,
                {
                    originChainId: origin.chainId,
                    originCurrency: origin.currency,
                    amount: amount.toString(),
                    mode: depositModeFor(source),
                    ...bridgeRequestField(),
                },
                signal,
            );
        },
        [authedApi, resolveDepositOrigin],
    );

    const confirm = useCallback(
        async (amount: bigint, source: DepositSource) => {
            if (lockRef.current) return;
            // a finished run (success or failed) is over: the next deposit may start on top of its
            // result screen. The HyperCore top-up button sits beside a just-settled deposit's
            // "Finish" and used to do nothing until Finish was pressed, because this refused to
            // start from `success`.
            if (
                state.status !== 'enteringAmount' &&
                state.status !== 'idle' &&
                state.status !== 'success' &&
                state.status !== 'failed'
            ) {
                return;
            }

            if (!authedApi) {
                setState((prev) => ({
                    ...prev,
                    status: 'failed',
                    amount,
                    error: 'Connect & sign in first',
                }));
                return;
            }

            lockRef.current = true;
            try {
                setState({
                    status: 'submitting',
                    amount,
                    error: null,
                    transferId: null,
                    jobId: null,
                    txHash: null,
                    progress: 5,
                    progressMessage: 'Submitting deposit job',
                    needsOnboarding: false,
                });
                logStep('Starting deposit', {
                    amount: amount.toString(),
                    source,
                });

                const origin = await resolveDepositOrigin(source);

                // RustSigner requires a fresh per-operation user signature for
                // the custodial fund movements the deposit saga pre-signs
                // (e.g. the HyperCore funding approval). The first attempt
                // carries an empty `fundAuthorizations` map (strict handshake
                // opt-in); the descriptors the backend answers with are
                // verified against this exact amount/owner, signed once per
                // slot, and the request retried with the filled-in map.
                const initialJob = await runWithFundAuthorization(
                    async (fundAuthorizations) =>
                        startDeposit(authedApi, {
                            originChainId: origin.chainId,
                            originCurrency: origin.currency,
                            amount: amount.toString(),
                            // the trading wallet's own USDC is moved by the backend saga; every
                            // other source is the user's EOA signing Relay steps.
                            mode: depositModeFor(source),
                            ...bridgeRequestField(),
                            fundAuthorizations,
                        }),
                    {
                        connectedAddress: address,
                        amount,
                        anchorChainId,
                        // a wallet-balance move IS the held amount; every other source sweeps it
                        hyperCoreSweep:
                            source === 'walletBalance'
                                ? 0n
                                : BigInt(
                                    Math.round(
                                        (heldOnHyperEvm?.() ?? 0) * 1_000_000,
                                    ),
                                ),
                        flow: 'deposit',
                        onPrompt: (method) =>
                            setState((prev) => ({
                                ...prev,
                                progressMessage:
                                    method === 'passkey'
                                        ? 'Confirm the deposit with your passkey'
                                        : 'Confirm the deposit in your wallet',
                            })),
                    },
                );

                setState((prev) => ({
                    ...prev,
                    jobId: initialJob.jobId,
                    transferId: transferIdFromJob(initialJob),
                    progress: initialJob.progress,
                    progressMessage: initialJob.message,
                    progressStep: initialJob.step,
                }));
                // client-side failures from here on belong to this job's diagnostic trail
                setActiveJobForErrorReports(initialJob.jobId, factionsApi);
                logStep('Deposit started', {
                    jobId: initialJob.jobId,
                    transferId: transferIdFromJob(initialJob),
                    status: initialJob.status,
                });

                await trackDepositJob(initialJob, amount, {
                    // nothing to sign on chain for a wallet-balance move: the job starts bridging.
                    signRequiredSteps: source !== 'walletBalance',
                });
            } catch (error) {
                // a rejected signature or a dropped relay step never reaches the backend, so
                // without this the job just looks frozen with no visible reason.
                reportJobException(error, { where: 'deposit' });
                failDeposit(error);
            } finally {
                // stop attributing later, unrelated failures to a finished operation
                setActiveJobForErrorReports(null, null);
                lockRef.current = false;
            }
        },
        [
            address,
            anchorChainId,
            authedApi,
            factionsApi,
            failDeposit,
            heldOnHyperEvm,
            resolveDepositOrigin,
            state.status,
            trackDepositJob,
        ],
    );

    const attach = useCallback(
        async (job: SpeedJobResponse) => {
            if (lockRef.current) return;
            if (!factionsApi || job.kind !== 'deposit') {
                return;
            }

            lockRef.current = true;
            try {
                const needsSignature = jobNeedsSignature(job, false);
                setState((prev) => ({
                    ...prev,
                    status: needsSignature
                        ? 'awaitingSignature'
                        : job.status === 'bridging'
                            ? 'bridging'
                            : 'submitting',
                    amount: amountFromJob(job) ?? prev.amount,
                    error: null,
                    transferId: transferIdFromJob(job),
                    jobId: job.jobId,
                    txHash: txHashFromJob(job) ?? prev.txHash,
                    progress: job.progress,
                    progressMessage: job.message,
                    progressStep: job.step,
                    needsOnboarding: false,
                }));
                const latestJob = await fetchSpeedJob(factionsApi, job.jobId);
                await trackDepositJob(
                    latestJob,
                    amountFromJob(latestJob) ?? amountFromJob(job),
                    // an attached job only observes; signatures run through
                    // continueActive, which the panel's own button drives
                    { signRequiredSteps: false },
                );
            } catch (error) {
                failDeposit(error);
            } finally {
                lockRef.current = false;
            }
        },
        [factionsApi, failDeposit, trackDepositJob],
    );

    const continueActive = useCallback(async () => {
        if (!state.jobId || !factionsApi || lockRef.current) {
            return;
        }
        lockRef.current = true;
        try {
            const latestJob = await fetchSpeedJob(factionsApi, state.jobId);
            if (latestJob.kind !== 'deposit') {
                return;
            }
            await trackDepositJob(
                latestJob,
                amountFromJob(latestJob) ?? state.amount,
                { signRequiredSteps: true },
            );
        } catch (error) {
            failDeposit(error);
        } finally {
            lockRef.current = false;
        }
    }, [factionsApi, failDeposit, state.amount, state.jobId, trackDepositJob]);

    const cancelActive = useCallback(async () => {
        const jobId = state.jobId;
        if (
            !jobId ||
            !authedApi ||
            lockRef.current ||
            state.status !== 'awaitingSignature'
        ) {
            return;
        }
        lockRef.current = true;
        try {
            await cancelSpeedJob(authedApi, jobId);
            logStep('Deposit cancelled', { jobId });
            setState(INITIAL_STATE);
            // the job list and the "another operation is running" gates read the row this closed
            onSuccess?.();
        } catch (error) {
            // refused: the deposit has left the wallet or the job moved on. the job itself is the
            // truth here, and the panel's attach effect re-syncs to it, so nothing is marked failed.
            logStep('Deposit cancel refused', {
                jobId,
                error: getErrorMessage(error),
            });
            toast.error('Could not cancel the deposit', {
                description: describeErrorText(error, { operation: 'Cancel' }),
                duration: TOAST_DURATION,
            });
        } finally {
            lockRef.current = false;
        }
    }, [authedApi, onSuccess, state.jobId, state.status]);

    return {
        state,
        status: state.status,
        open,
        confirm,
        cancel,
        retry,
        reset,
        attach,
        continueActive,
        cancelActive,
        quoteDeposit,
        canContinue: state.status === 'awaitingSignature' && !lockRef.current,
        canCancel: state.status === 'awaitingSignature' && !lockRef.current,
        isActive: state.status !== 'idle' && state.status !== 'success',
    };
}
